import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash, Key, UserCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersDialog({ open, onOpenChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // new-user form
  const [newName, setNewName] = useState("");
  const [newPin, setNewPin] = useState("");

  // pin-edit map: { [user_id]: "0000" }
  const [pinEdits, setPinEdits] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/users`);
      setUsers(data);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ ΦΟΡΤΩΣΗΣ ΧΡΗΣΤΩΝ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const createUser = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/auth/users`, {
        display_name: newName.trim(),
        pin: newPin.trim(),
        role: "employee",
      });
      setNewName("");
      setNewPin("");
      toast.success("ΠΡΟΣΤΕΘΗΚΕ");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Σφάλμα";
      toast.error(typeof msg === "string" ? msg : "Σφάλμα");
    }
  };

  const savePin = async (userId) => {
    const val = (pinEdits[userId] ?? "").trim();
    if (val !== "" && !(val.length === 4 && /^\d{4}$/.test(val))) {
      toast.error("PIN 4 ΨΗΦΙΑ");
      return;
    }
    try {
      await axios.put(`${API}/auth/users/${userId}`, { pin: val });
      toast.success("ΑΠΟΘΗΚΕΥΤΗΚΕ");
      setPinEdits((m) => {
        const { [userId]: _, ...rest } = m;
        return rest;
      });
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Σφάλμα";
      toast.error(typeof msg === "string" ? msg : "Σφάλμα");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`ΔΙΑΓΡΑΦΗ ΧΡΗΣΤΗ "${u.display_name}";`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`);
      toast.success("ΔΙΑΓΡΑΦΗΚΕ");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Σφάλμα";
      toast.error(typeof msg === "string" ? msg : "Σφάλμα");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="users-dialog"
      >
        <div className="px-7 pt-6 pb-3 bg-ink text-white">
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold flex items-center gap-2">
              <ShieldCheck size={24} weight="bold" />
              ΔΙΑΧΕΙΡΙΣΗ ΧΡΗΣΤΩΝ
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              ΠΡΟΣΘΗΚΗ / ΔΙΑΓΡΑΦΗ / ΕΠΕΞΕΡΓΑΣΙΑ PIN
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-5 bg-white space-y-6">
          {/* New user */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70 mb-2">
              ΝΕΟΣ ΥΠΑΛΛΗΛΟΣ
            </div>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value.toUpperCase())}
                placeholder="ΟΝΟΜΑ"
                data-testid="new-user-name"
                className="flex-1 px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial uppercase"
              />
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={newPin}
                onChange={(e) =>
                  setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                placeholder="PIN (προαιρ.)"
                data-testid="new-user-pin"
                className="w-28 px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial text-center tracking-widest"
              />
              <button
                onClick={createUser}
                disabled={!newName.trim()}
                data-testid="new-user-submit"
                className={`px-4 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1 transition-all ${
                  newName.trim()
                    ? "bg-ink text-white hover:bg-ink/85 active:scale-95"
                    : "bg-ink/20 text-white/70 cursor-not-allowed"
                }`}
              >
                <Plus size={14} weight="bold" />
                ΠΡΟΣΘΗΚΗ
              </button>
            </div>
          </div>

          {/* List */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70 mb-2">
              ΧΡΗΣΤΕΣ ({users.length})
            </div>
            <div className="space-y-2" data-testid="users-list">
              {loading && (
                <p className="text-xs text-ink/50 italic">ΦΟΡΤΩΣΗ...</p>
              )}
              {!loading && users.length === 0 && (
                <p className="text-xs text-ink/50 italic">ΧΩΡΙΣ ΧΡΗΣΤΕΣ</p>
              )}
              {users.map((u) => {
                const isSup = u.role === "supervisor";
                const pinVal = pinEdits[u.id];
                const hasUnsavedPin =
                  pinVal !== undefined &&
                  pinVal !== (u.has_pin ? "••••" : "");
                return (
                  <div
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${
                      isSup
                        ? "bg-amber-50 border-amber-200"
                        : "bg-white border-ink/10"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${
                        isSup ? "bg-amber-500 text-white" : "bg-ink/10 text-ink"
                      }`}
                    >
                      {isSup ? (
                        <ShieldCheck size={18} weight="bold" />
                      ) : (
                        <UserCircle size={20} weight="bold" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink uppercase tracking-wide truncate">
                        {u.display_name}
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-ink/50 font-bold">
                        {isSup
                          ? `SUPERVISOR · ${u.username}`
                          : u.has_pin
                          ? "PIN ΟΡΙΣΜΕΝΟ"
                          : "ΧΩΡΙΣ PIN"}
                      </div>
                    </div>

                    {!isSup && (
                      <div className="flex items-center gap-1.5">
                        <Key size={14} weight="bold" className="text-ink/40" />
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d{4}"
                          maxLength={4}
                          value={pinVal ?? ""}
                          placeholder={u.has_pin ? "••••" : "PIN"}
                          onChange={(e) =>
                            setPinEdits((m) => ({
                              ...m,
                              [u.id]: e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 4),
                            }))
                          }
                          data-testid={`user-pin-input-${u.id}`}
                          className="w-20 px-2 py-1.5 rounded-lg border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial text-center tracking-widest"
                        />
                        <button
                          onClick={() => savePin(u.id)}
                          disabled={!hasUnsavedPin && pinVal === undefined}
                          data-testid={`user-pin-save-${u.id}`}
                          className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                            pinVal !== undefined
                              ? "bg-ink text-white hover:bg-ink/85 active:scale-95"
                              : "bg-ink/10 text-ink/30 cursor-not-allowed"
                          }`}
                        >
                          ΑΠΟΘ.
                        </button>
                      </div>
                    )}

                    {!isSup && (
                      <button
                        onClick={() => removeUser(u)}
                        data-testid={`user-delete-${u.id}`}
                        className="p-2 text-red-600/70 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="ΔΙΑΓΡΑΦΗ"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
